import React, { useState } from 'react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';

const OutgoingPaymentForm = ({ accounts, onSuccess, onCancel, isExpenseMode = false }) => {
  const { organization } = useOrganization();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'CASH',
    cash_account_id: '',
    credit_description: '',
    debit_account_id: '',
    debit_description: '',
    pay_later: false,
    due_date: ''
  });
  const [loading, setLoading] = useState(false);

  const cashAccounts = accounts.filter(acc => 
    acc.type === 'ASSET' && 
    (acc.name.toLowerCase().includes('cash') || 
     acc.name.toLowerCase().includes('bank') || 
     acc.name.toLowerCase().includes('till'))
  );
  const expenseAccounts = accounts.filter(acc => acc.type === 'EXPENSE');

  React.useEffect(() => {
    setupExpenseAccountsIfNeeded();
  }, []);

  const setupExpenseAccountsIfNeeded = async () => {
    if (expenseAccounts.length === 0) {
      try {
        await axios.post('/api/accounts/setup-expense');
        toast.success('Default expense accounts created');
        if (onSuccess) onSuccess();
      } catch (error) {
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isExpenseMode) {
        // Create expense record (which automatically creates accounting transactions)
        const expenseData = {
          categoryId: parseInt(formData.debit_account_id),
          description: formData.debit_description,
          amount: parseFloat(formData.amount),
          expenseDate: formData.date,
          expenseType: 'manual'
        };
        
        await axios.post('/api/expenses', expenseData);
        toast.success('Expense recorded successfully');
      } else {
        // Create manual transactions for non-expense mode
        const amount = parseFloat(formData.amount);
        const _refNumber = `PAY${formData.date.replace(/-/g, '')}001`;
        
        // Create expense transaction (debit)
        await axios.post('/api/transactions/manual', {
          date: formData.date,
          entries: [
            {
              account_id: parseInt(formData.debit_account_id),
              description: formData.debit_description,
              debit: amount,
              credit: 0
            },
            {
              account_id: parseInt(formData.cash_account_id),
              description: formData.credit_description,
              debit: 0,
              credit: amount
            }
          ]
        });
        
        toast.success('Outgoing payment recorded successfully');
      }
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      <h3 className="text-lg font-semibold mb-4">{isExpenseMode ? 'Record Expense' : 'Record Outgoing Payment'}</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              step="0.01"
              min="0"
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="CASH">Cash</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CREDIT_CARD">Credit Card</option>
              <option value="CHEQUE">Cheque</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cash Account *</label>
            <select
              name="cash_account_id"
              value={formData.cash_account_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Cash Account</option>
              {cashAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} ({formatCurrency(parseFloat(account.balance) || 0, organization?.currency_code)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expense Account *</label>
            <select
              name="debit_account_id"
              value={formData.debit_account_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Expense Account</option>
              {expenseAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Credit Description *</label>
            <input
              type="text"
              name="credit_description"
              value={formData.credit_description}
              onChange={handleChange}
              placeholder="e.g., Paid for office supplies"
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Debit Description *</label>
            <input
              type="text"
              name="debit_description"
              value={formData.debit_description}
              onChange={handleChange}
              placeholder="e.g., Office Supplies Expense"
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="pay_later"
            checked={formData.pay_later}
            onChange={handleChange}
            className="mr-2"
          />
          <label className="text-sm text-gray-700">Pay Later</label>
        </div>

        {formData.pay_later && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Recording...' : (isExpenseMode ? 'Record Expense' : 'Record Payment')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OutgoingPaymentForm;
